import { KeycloakContext } from '@shared/config/context/KeycloakContext';
import { AppChildrenNavItem, AppNavItem, Credential, RouteAccess } from '@shared/types/api';
import { LanguageTypes, NavItem } from '@uni-space/uni-components';
import { JSX, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

interface RequireAuthProps {
  role?: Credential;
  routeAccess?: RouteAccess[];
  children: JSX.Element;
}
export type UserCredentials = {
  roles: Credential[];
  email: string;
  byRole: Partial<Record<Credential, RouteAccess[]>>;
  hasRoleAccess: (role: Credential, need: RouteAccess[]) => boolean;
  hasRoleAccessAny: (role: Credential, need: RouteAccess[]) => boolean;
};

export const useUserCredentials = (): UserCredentials => {
  const { userInfo } = useContext(KeycloakContext);

  return useMemo<UserCredentials>(() => {
    if (!userInfo) {
      return {
        roles: [],
        email: '',
        byRole: {},
        hasRoleAccess: () => false,
        hasRoleAccessAny: () => false,
      };
    }

    const email = userInfo.email ?? '';

    const credValues = new Set(Object.values(Credential) as Credential[]);
    const accessValues = new Set(Object.values(RouteAccess) as RouteAccess[]);

    const roles: Credential[] = [];
    const byRole: Partial<Record<Credential, RouteAccess[]>> = {};

    for (const [key, raw] of Object.entries(userInfo)) {
      const maybeCred = key as Credential;
      if (!credValues.has(maybeCred)) continue;

      const arr = Array.isArray(raw) ? raw : [];
      const normalized = arr.filter((v): v is RouteAccess => accessValues.has(v as RouteAccess));

      if (normalized.length > 0) {
        byRole[maybeCred] = Array.from(new Set(normalized));
      }

      if (normalized.includes(RouteAccess.view)) {
        roles.push(maybeCred);
      }
    }

    const hasRoleAccess = (role: Credential, need: RouteAccess[]) => {
      const granted = new Set(byRole[role] ?? []);
      return need.every((n) => granted.has(n));
    };

    const hasRoleAccessAny = (role: Credential, need: RouteAccess[]) => {
      const granted = new Set(byRole[role] ?? []);
      return need.some((n) => granted.has(n));
    };

    return { roles, email, byRole, hasRoleAccess, hasRoleAccessAny };
  }, [userInfo]);
};

export function RequireAuth({ role, routeAccess = [RouteAccess.view], children }: RequireAuthProps) {
  const location = useLocation();
  const { roles: userRoles, hasRoleAccessAny } = useUserCredentials();
  const { i18n } = useTranslation();

  const messageError = {
    [LanguageTypes.en]: "You don't have the necessary access rights.",
    [LanguageTypes.cn]: '您没有必要的访问权限。',
    [LanguageTypes.ru]: 'У вас нет нужных прав доступа',
  };

  if (!userRoles || !userRoles.length) return <>{messageError[i18n.language]}</>;

  if (!role) return children;

  const hasAccess = hasRoleAccessAny(role, routeAccess);
  if (!hasAccess) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
}

export function filterNavItemsByParentAccess(
  items: AppNavItem[],
  roles: Credential[],
  hasRoleAccessAny: (role: Credential, need: RouteAccess[]) => boolean,
): AppNavItem[] {
  const filterChildren = (
    nodes: AppChildrenNavItem[] | undefined,
    currentParent: Credential,
  ): AppChildrenNavItem[] | undefined => {
    if (!nodes || nodes.length === 0) return undefined;

    const out: AppChildrenNavItem[] = [];

    for (const node of nodes) {
      const required = node.accessByCredentialValue ?? [];

      if (!hasRoleAccessAny(currentParent, required)) {
        continue;
      }

      out.push({ ...node });
    }

    return out.length ? out : undefined;
  };

  return items.reduce<AppNavItem[]>((acc, item) => {
    if (!item.access) {
      acc.push({ ...item });
      return acc;
    }

    const allowedRolesForParent = roles.includes(item.access);

    if (!allowedRolesForParent) {
      return acc;
    }

    const children = filterChildren(item.children, item.access);

    acc.push({ ...item, children });
    return acc;
  }, []);
}
